import { useCallback, useMemo, useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import type { StoryPhoto } from '../components/StoryGallery';

export type SlideshowMilestone = {
  year: string;
  title?: string;
  locations?: string;
  photos?: StoryPhoto[];
};

type AdvanceResult =
  | { status: 'empty' }
  | { status: 'end' }
  | { status: 'advanced'; nextIndex: number; nextPhoto?: StoryPhoto };

type SlideshowController = {
  activeIndex: number;
  activePhotoIndex: number;
  requestedPhotoIndex: number | null;
  advanceSignal: number;
  activeMilestone: SlideshowMilestone;
  activePhotos: StoryPhoto[];
  setActiveIndex: Dispatch<SetStateAction<number>>;
  setActivePhotoIndex: Dispatch<SetStateAction<number>>;
  setRequestedPhotoIndex: Dispatch<SetStateAction<number | null>>;
  bumpAdvanceSignal: () => void;
  advancePhoto: () => AdvanceResult;
};

export const useSlideshowController = <T extends SlideshowMilestone>(
  milestones: T[]
): SlideshowController & { activeMilestone: T } => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [activePhotoIndex, setActivePhotoIndex] = useState(0);
  const [requestedPhotoIndex, setRequestedPhotoIndex] = useState<number | null>(null);
  const [advanceSignal, setAdvanceSignal] = useState(0);

  const activeMilestone = milestones[activeIndex];
  const activePhotos = useMemo(
    () => (activeMilestone?.photos as StoryPhoto[]) ?? [],
    [activeMilestone]
  );

  const bumpAdvanceSignal = useCallback(() => {
    setAdvanceSignal((prev) => prev + 1);
  }, []);

  const advancePhoto = useCallback((): AdvanceResult => {
    if (activePhotos.length === 0) return { status: 'empty' };
    if (activePhotoIndex >= activePhotos.length - 1) return { status: 'end' };
    const nextIndex = activePhotoIndex + 1;
    setRequestedPhotoIndex(nextIndex);
    setActivePhotoIndex(nextIndex);
    bumpAdvanceSignal();
    return { status: 'advanced', nextIndex, nextPhoto: activePhotos[nextIndex] };
  }, [activePhotos, activePhotoIndex, bumpAdvanceSignal]);

  return {
    activeIndex,
    activePhotoIndex,
    requestedPhotoIndex,
    advanceSignal,
    activeMilestone,
    activePhotos,
    setActiveIndex,
    setActivePhotoIndex,
    setRequestedPhotoIndex,
    bumpAdvanceSignal,
    advancePhoto,
  };
};
